import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import Input from 'views/ui/form_elements/input';
import { loadLocales, t } from 'services/translation';
import SessionStore from 'stores/shared/session';
import CountryDropdown from 'views/ui/form_elements/dropdown/country';
import classnames from 'classnames';
import StripeService from 'services/stripe';
import { StripeCardElement } from 'ui_system/inputs/stripe_card';
import BookingRequestActionCreator from 'action_creators/booking_request';

export default class Payment extends React.Component {
  constructor(props) {
    super(props);
    StripeService._init(props.managedAccountToken, props.stripeTheme.fonts);
  }

  componentDidMount() {
    const { stripeTheme } = this.props;
    // remove fonts from theme to avoid passing in invalid styles
    const { fonts, ...stripeStyle } = stripeTheme;
    const card = window.elements.create('cardNumber', {
      style: stripeStyle,
      disableLink: true,
    });
    const cardExpiry = window.elements.create('cardExpiry', {
      style: stripeStyle,
      placeholder: t('MM_YY'),
    });
    const cardCvc = window.elements.create('cardCvc', {
      style: stripeStyle,
      placeholder: t('CARD_CVC'),
    });
    const postalCodeElement = window.elements.create('postalCode', {
      style: stripeStyle,
      placeholder: t('ZIP_POSTAL_CODE'),
    });

    BookingRequestActionCreator.addCardElement(card);
    card.mount('#stripe-card-element');
    card.addEventListener('change', this.formProps('cardNumber').onChange);
    cardExpiry.mount('#stripe-expiry-element');
    cardExpiry.addEventListener(
      'change',
      this.formProps('cardExpiry').onChange
    );
    cardCvc.mount('#stripe-cvc-element');
    cardCvc.addEventListener('change', this.formProps('cardCvc').onChange);

    postalCodeElement.mount('#stripe-postalCode-element');
    postalCodeElement.addEventListener(
      'change',
      this.formProps('postalCode').onChange
    );

    this.payment.addEventListener(
      'focus',
      () => this.props.googleTagManagerCheckoutProgress(3),
      {
        capture: true,
        once: true,
      }
    );
  }

  formProps(fieldName, otherProps) {
    const { errors, fields, onChange } = this.props;
    const translatedErrors =
      errors.get(fieldName) !== undefined
        ? this.translateError(fieldName)
        : errors.get(fieldName);

    return {
      ...otherProps,
      name: fieldName,
      value: fields.get(fieldName),
      errors: translatedErrors,
      onChange,
    };
  }

  translateError(fieldName) {
    const { errors } = this.props;
    loadLocales(SessionStore.state.get('i18n'));

    return errors.get(fieldName).map((error) => {
      const cleanedFieldName =
        fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
      const translatedFieldName = t(cleanedFieldName.replace(/_/g, ' '));
      return `${translatedFieldName} ${t('CANT_BE_BLANK')}`;
    });
  }

  zipCodeLabel() {
    const { fields } = this.props;
    return fields.get('billing_country') === 'US'
      ? t('ZIP_CODE')
      : t('POSTAL_CODE');
  }

  stripeCardElementErrors() {
    const { errors } = this.props;
    return errors.get('stripe_card_element');
  }

  renderUsOnlyGuide() {
    const { guide_us_only: guideUSOnly } = this.props;

    if (guideUSOnly) {
      return (
        <span className="us-only-guide">
          We currently only accept online bookings from the United States.
        </span>
      );
    }
  }

  getErrorFor(str) {
    const { errors } = this.props;
    if ('get' in errors) {
      return errors.get(str);
    }
    return errors[str];
  }

  render() {
    const { disabled, errors, guide_us_only: guideUSOnly } = this.props;
    const sectionClasses = classnames('CheckoutPayment', 'CheckoutSection', {
      'CheckoutSection-disabled': disabled,
    });

    const postalCodeClass = this.getErrorFor('postalCode')
      ? 'postal-error'
      : '';
    return (
      <div
        className={sectionClasses}
        ref={(c) => {
          this.payment = c;
        }}
      >
        <div className="row">
          <form autoComplete="off">
            <label htmlFor="cardHolderName" id="cardholder-name-field">
              {t('CARDHOLDERS_NAME')}
            </label>
            <div className="ui column grid">
              <div className="six wide column">
                <Input
                  {...this.formProps('cardholder_first_name', {
                    placeholder: t('FIRST'),
                    id: 'cardHolderName',
                    ariaLabelledBy: 'cardholder-name-field',
                    autoComplete: 'given-name',
                  })}
                />
              </div>
              <div className="six wide column">
                <Input
                  {...this.formProps('cardholder_last_name', {
                    placeholder: t('LAST'),
                    ariaLabelledBy: 'cardholder-name-field',
                    autoComplete: 'family-name',
                  })}
                />
              </div>
            </div>
          </form>
        </div>
        <div className="row">
          <form autoComplete="off">
            <StripeCardElement
              errors={errors}
              onChange={this.formProps('stripe_card_element').onChange}
            />
          </form>
        </div>
        <div className="row">
          <form autoComplete="off">
            <label htmlFor="billingAddress" id="billing-address-field">
              {t('BILLING_ADDRESS')}
            </label>
            <div style={{ marginBottom: 10 }}>
              <Input
                {...this.formProps('billing_address_line_1', {
                  'data-testid': 'billing-addr1-input',
                  placeholder: t('ADDRESS_LINE_1'),
                  id: 'billingAddress',
                  ariaLabelledBy: 'billing-address-field',
                  autoComplete: 'address-line1',
                })}
              />
            </div>
            <Input
              {...this.formProps('billing_address_line_2', {
                'data-testid': 'billing-addr2-input',
                placeholder: t('ADDRESS_LINE_2'),
                ariaLabelledBy: 'billing-address-field',
                autoComplete: 'address-line2',
              })}
            />
          </form>
        </div>
        <div className="row">
          <form autoComplete="off">
            <div className="ui column grid">
              <div className="six wide column">
                <label htmlFor="city" id="city-field">
                  {t('CITY')}
                </label>
                <Input
                  {...this.formProps('billing_city', {
                    'data-testid': 'city-input',
                    placeholder: t('CITY'),
                    id: 'city',
                    ariaLabelledBy: 'city-field',
                    autoComplete: 'address-level2',
                  })}
                />
              </div>
              <div className="six wide column">
                <label htmlFor="stateRegion" id="state-region-field">{`${t(
                  'STATE'
                )}/${t('REGION')}`}</label>
                <Input
                  {...this.formProps('billing_region', {
                    'data-testid': 'state-input',
                    placeholder: t('STATE'),
                    id: 'stateRegion',
                    ariaLabelledBy: 'state-region-field',
                    autoComplete: 'address-level1',
                  })}
                />
              </div>
            </div>
          </form>
        </div>
        <div className="row country">
          <div className="ui column grid">
            <div className="six wide column">
              <form autoComplete="off" id="country-field">
                <label aria-label="Select country">{t('COUNTRY')}</label>
                <CountryDropdown
                  {...this.formProps('billing_country', {
                    'data-testid': 'country-input',
                    guide_us_only: guideUSOnly,
                    id: 'country',
                    ariaLabelledBy: 'country-field',
                  })}
                />
              </form>
            </div>
            <div className="six wide column">
              <form
                autoComplete="off"
                onSubmit={(e) => e.preventDefault()}
                className={postalCodeClass}
              >
                <label>{this.zipCodeLabel()}</label>
                <div
                  className={
                    this.getErrorFor('postalCode')
                      ? 'postal-element error'
                      : 'postal-element '
                  }
                  id="stripe-postalCode-element"
                  data-testid="zip-code-input"
                />
                <span className="input-error">
                  {this.getErrorFor('postalCode') || ''}
                </span>
              </form>
            </div>
          </div>
          {this.renderUsOnlyGuide()}
        </div>
      </div>
    );
  }
}

Payment.propTypes = {
  fields: PropTypes.object,
  errors: PropTypes.object,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  managedAccountToken: PropTypes.string,
  stripeTheme: PropTypes.object,
  stepNo: PropTypes.number,
  googleTagManagerCheckoutProgress: PropTypes.func,
};
